import React from 'react';
import { notificationBannerThemes } from './BannerConsts';
import cancel from './icons/Cancel.svg';
import { Button, Tooltip } from '@material-ui/core';

/**
 * Notification Banner designed to raise users attention and allow them to react to specific events
 *
 * @param {string} title           Banner title
 *
 * @param {string} content         Banner text
 *
 * @param {string} theme           Optional - banner theme, as a notificationBannerThemes key:
 *                                     'primary' for general message (default),
 *                                     'warn' for warning message,
 *                                     'error' for error message
 *                                     'info' for information
 *
 * @param {bool} closable          Optional - set if the banner will have a close button.
 *                                 should be configured together with onClose handler
 *
 * @param {string} actionText      Optional - set action button with text. should be configured together with onAction handler
 *
 * @param {string} height          Optional - set custom height as a css height prop, for example, '50px'
 *
 * @param {string} width           Optional - set custom width as a css width prop, for example, '50px'
 *
 * @param {string} padding         Optional - set custom padding as a css padding prop, for example, '10px'
 *
 * @param {string} iconSize        Optional - set banner icon size, for example: '16px'
 *
 * @param {string} messagePadding  Optional - set custom icon padding, for example: '2px'
 *
 * @param {string} actionTooltip   Optional - set tooltip for action button, for example: 'Click to start'
 *
 * @param {boolean} disableAction   Optional - show a disabled action button
 *
 * @param {function} onAction      Optional - handler for action click event
 *
 * @param {function} onClose       Optional - handler for close click event
 */
const NotificationBanner = ({
    title,
    content,
    theme,
    closable,
    actionText,
    height,
    width,
    padding,
    iconSize,
    messagePadding,
    onAction = () => {},
    onClose = () => {},
    disableAction,
    actionTooltip
}) => {
    const { color, icon } = notificationBannerThemes[theme] || notificationBannerThemes.primary;

    const calcCustomStyle = () => {
        const style = {};
        if (width) style.width = width;
        if (height) style.height = height;
        if (padding) style.padding = padding;
        return style;
    };

    return (
        <div
            style={calcCustomStyle()}
            className={`${padding ? '' : 'p-6'} ${width ? '' : 'w-full'} ${
                height ? '' : content ? 'h-22' : 'h-16'
            } flex flex-row justify-between  bg-white rounded-lg border border-${color}`}>
            <div className={`flex items-center justify-between w-full`}>
                <img
                    style={{ width: iconSize, height: iconSize }}
                    className={iconSize ? '' : 'w-6 h-6'}
                    src={icon}
                    alt=""
                />
                <div
                    style={{ padding: messagePadding }}
                    className={`flex flex-col  w-full justify-center ${messagePadding ? '' : 'px-4'}`}>
                    <div className="font-medium text-base pb-0.5">{title}</div>
                    <div className="text-sf-light-700 text-xs">{content}</div>
                </div>
            </div>
            {(actionText || closable) && (
                <div className="flex items-center justify-center">
                    {actionText && (
                        <Tooltip
                            title={actionTooltip}
                            enterDelay={100}
                            placement={'top'}
                            classes={{
                                tooltip: 'bg-black text-xs h-fix w-64 pb-1.5 pt-1.5 pl-3 pr-3 mb-1'
                            }}>
                            <Button
                                disableRipple
                                onClick={disableAction ? () => {} : () => onAction()}
                                className={`min-w-26 px-3 h-10 bg-sf-primary text-white rounded-lg text-sm normal-case ${
                                    disableAction ? 'cursor-not-allowed opacity-50' : ''
                                }`}>
                                {actionText}
                            </Button>
                        </Tooltip>
                    )}
                    {closable && (
                        <img onClick={() => onClose()} className="w-4 h-4 cursor-pointer ml-4" src={cancel} alt="" />
                    )}
                </div>
            )}
        </div>
    );
};

export default NotificationBanner;
